import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, WifiOff } from 'lucide-react';

/**
 * Offline notice and install prompt (#28).
 *
 * Both matter for the same reason: the users are in the field on poor
 * connections. The offline banner exists because TanStack Query will happily
 * serve a cached lead list with no indication it is stale, and a rep quoting a
 * price from a twenty-minute-old cache has a real problem. Telling them is
 * cheaper than trying to prevent it.
 *
 * The install prompt is deferred rather than fired on load: browsers only allow
 * it after a user gesture, and an install dialog on first paint is the kind of
 * thing people dismiss reflexively and never see again.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'crm.installPromptDismissed';

export function ConnectionBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      // Private browsing, or storage blocked. Treat it as "not dismissed" — the
      // banner is dismissible anyway, so the worst case is seeing it twice.
      return false;
    }
  });

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    const onBeforeInstall = (event: Event) => {
      // Chrome shows its own mini-infobar unless this is prevented; we want the
      // prompt where the user can see why they would want it.
      event.preventDefault();
      setInstallEvent(event as InstallPromptEvent);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    await installEvent.userChoice;
    // The event can only be used once, whatever the outcome.
    setInstallEvent(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Not worth surfacing: the banner is gone for this session either way.
    }
  };

  if (!online) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-sm text-amber-900 print:hidden"
      >
        <WifiOff className="size-4 shrink-0" aria-hidden="true" />
        {t('pwa.offline')}
      </div>
    );
  }

  if (installEvent && !dismissed) {
    return (
      <div className="flex flex-wrap items-center gap-2 bg-brand-50 px-4 py-2 text-sm text-brand-900 print:hidden">
        <Download className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">{t('pwa.installHint')}</span>
        <button
          type="button"
          onClick={() => void install()}
          className="rounded-lg bg-brand-700 px-3 py-1 text-xs font-medium text-white hover:bg-brand-800"
        >
          {t('pwa.install')}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-3 py-1 text-xs font-medium text-brand-800 hover:bg-brand-100"
        >
          {t('pwa.dismiss')}
        </button>
      </div>
    );
  }

  return null;
}
